import { useState, useEffect } from 'react';
import { fetchFieldOptions } from '../services/baserow/fields';
import { BASEROW_CONFIG } from '../services/baserow/config';

export interface Topic {
  id: number;
  value: string;
}

const CACHE_KEY = 'baserow_topics';
const CACHE_DURATION = 1000 * 60 * 5; // 5 minutes

interface CachedData {
  topics: Topic[];
  timestamp: number;
}

export function useTopics() {
  const [topics, setTopics] = useState<Topic[]>(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { topics, timestamp }: CachedData = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return topics;
        }
      }
    } catch (e) {
      console.warn('Failed to load cached topics:', e);
    }
    return [];
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadTopics = async () => {
      try {
        setLoading(true);
        setError(null);
        const options = await fetchFieldOptions(BASEROW_CONFIG.FIELDS.TOPIC);
        
        if (mounted) {
          const newTopics = options.map(({ id, value }) => ({ id, value }));
          setTopics(newTopics);
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            topics: newTopics,
            timestamp: Date.now()
          }));
        }
      } catch (err) {
        if (mounted) {
          const errorMessage = err instanceof Error ? err.message : 'Failed to load topics';
          console.error('Topics loading error:', errorMessage);
          setError(errorMessage);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadTopics();

    return () => {
      mounted = false;
    };
  }, []);

  const reload = () => {
    localStorage.removeItem(CACHE_KEY);
    setTopics([]);
    setLoading(true);
  };

  return { topics, loading, error, reload };
}